import React, { useEffect, useState, useCallback } from 'react';
import { RouteComponentProps, useParams } from 'react-router-dom';
import axios from '../../axios';

import { IMarketResearchTask, IAgentTask, IMarketResearchInsight } from '../../interfaces/domain';
import { EMarketResearchStatus } from '../../enums';
import { Routes } from '../../classes/Routes';

import Alert from '../../components/UI/Alert/Alert';
import Button, { EButtonColor, EButtonSize } from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import AgentTaskCard from './AgentTaskCard';
import AddAgentTaskPanel from './AddAgentTaskPanel';
import WorkbookSourcesPanel from './WorkbookSourcesPanel';

import classes from './MarketResearchWorkbench.module.css';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Luonnos', color: '#94a3b8' },
  IN_PROGRESS: { label: 'Käynnissä', color: '#f59e0b' },
  COMPLETED: { label: 'Valmis', color: '#22c55e' },
  NO_GO: { label: 'No-Go', color: '#ef4444' },
};

const MarketResearchWorkbench: React.FC<RouteComponentProps> = ({ history }) => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const [task, setTask] = useState<IMarketResearchTask | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [notes, setNotes] = useState('');

  const fetchTask = useCallback(async (silent = false) => {
    if (isNew) return;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`/json/market-research/${id}`);
      const data = response.data as IMarketResearchTask;
      setTask((prev) => {
        if (!prev || !silent) return data;
        return {
          ...prev,
          status: data.status,
          agentTasks: data.agentTasks,
          insights: data.insights,
          workbookSources: data.workbookSources,
        };
      });
      if (!silent) {
        setName(data.name || '');
        setDescription(data.description || '');
        setConclusion(data.conclusion || '');
        setNotes(data.notes || '');
      }
      setError(null);
    } catch (err: any) {
      if (!silent) {
        setError(err.response?.data || 'Virhe ladattaessa tutkimusta');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  // Auto-refresh when tasks are PENDING, PROCESSING or POLLING
  useEffect(() => {
    if (!task) return;
    const hasRunning = task.agentTasks?.some(
      (t) => t.status === 'PENDING' || t.status === 'PROCESSING' || t.status === 'POLLING'
    );
    if (!hasRunning) return;
    const interval = setInterval(() => fetchTask(true), 15000);
    return () => clearInterval(interval);
  }, [task, fetchTask]);

  const handleSaveDraft = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Nimi ja kuvaus ovat pakollisia');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await axios.post('/json/market-research', {
        action: 'draft',
        id: isNew ? undefined : id,
        name,
        description,
      });
      if (isNew) {
        history.replace(Routes.MARKET_RESEARCH_TASK(response.data.id));
      } else {
        setTask(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data || 'Virhe tallennettaessa');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFields = async () => {
    if (!task) return;
    setSaving(true);
    setError(null);
    try {
      const response = await axios.post('/json/market-research', {
        action: 'update',
        id: task.id,
        conclusion,
        notes,
      });
      setTask((prev) => prev ? { ...prev, ...response.data } : response.data);
    } catch (err: any) {
      setError(err.response?.data || 'Virhe päivitettäessä');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAgentTask = async (
    taskType: string,
    modelType: string,
    customQuery: string,
    selectedSourceIds?: number[],
    selectedInsightIds?: number[]
  ) => {
    if (!task) return;
    setError(null);
    try {
      await axios.post('/json/market-research', {
        action: 'add-agent-task',
        taskId: task.id,
        taskType,
        modelType,
        customQuery: customQuery || undefined,
        selectedSourceIds: selectedSourceIds || undefined,
        selectedInsightIds: selectedInsightIds || undefined,
      });
      await fetchTask();
    } catch (err: any) {
      setError(err.response?.data || 'Virhe lisättäessä tehtävää');
    }
  };

  const handleDeleteAgentTask = async (agentTaskId: string) => {
    setError(null);
    const originalTask = task;
    // Optimistic update
    setTask((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        agentTasks: prev.agentTasks?.filter((t) => String(t.id) !== String(agentTaskId)),
      };
    });
    try {
      await axios.post('/json/market-research', {
        action: 'delete-agent-task',
        id: agentTaskId,
      });
      await fetchTask(true);
    } catch (err: any) {
      setError(err.response?.data || 'Virhe poistettaessa tehtävää');
      // Rollback on error
      setTask(originalTask);
    }
  };

  const handleRefreshAgentTask = async (agentTaskId: string) => {
    await axios.post('/json/agent-tasks', {
      action: 'poll-task',
      id: parseInt(agentTaskId),
    });
    await fetchTask(true);
  };

  const handleSetNoGo = async () => {
    if (!task || !window.confirm('Haluatko merkitä tämän tutkimuksen NO-GO-tilaan?')) return;
    setSaving(true);
    try {
      await axios.post('/json/market-research', {
        action: 'update',
        id: task.id,
        status: EMarketResearchStatus.NO_GO,
        conclusion: conclusion || 'Toimialaa ei avata.',
      });
      await fetchTask();
    } catch (err: any) {
      setError(err.response?.data || 'Virhe päivitettäessä tilaa');
    } finally {
      setSaving(false);
    }
  };

  const findInsightForTask = (agentTask: IAgentTask): IMarketResearchInsight | undefined => {
    return task?.insights?.find((i) => i.agentTaskId === agentTask.id);
  };

  if (loading) {
    return <Container><Spinner /></Container>;
  }

  const statusInfo = STATUS_LABELS[task?.status || 'DRAFT'] || STATUS_LABELS.DRAFT;
  const agentTasks = task?.agentTasks || [];

  return (
    <Container>
      {error && <Alert>{error}</Alert>}

      {/* Header */}
      <div className={classes.topBar}>
        <Button color={EButtonColor.SECONDARY} onClick={() => history.push(Routes.MARKET_RESEARCH_TASKS)}>
          ← Takaisin
        </Button>
        {task && (
          <span className={classes.statusBadge} style={{ backgroundColor: statusInfo.color }}>
            {statusInfo.label}
          </span>
        )}
      </div>

      {/* Basic info section */}
      <div className={classes.section}>
        <h2 className={classes.sectionTitle}>{isNew ? 'Uusi tutkimus' : 'Tutkimuksen perustiedot'}</h2>
        <div className={classes.formGrid}>
          <div className={classes.field}>
            <label>Nimi</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Esim. Huoneistoalan tarkistusmittaus"
              className={classes.input}
            />
          </div>
          <div className={classes.field}>
            <label>Kuvaus</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Kuvaa tutkimuksen tavoitteet ja rajaukset..."
              className={classes.textarea}
              rows={3}
            />
          </div>
        </div>
        <div className={classes.actions}>
          <Button onClick={handleSaveDraft} disabled={saving}>
            {saving ? 'Tallennetaan...' : isNew ? 'Luo tutkimus' : 'Tallenna perustiedot'}
          </Button>
        </div>
      </div>

      {/* Workbench — only show after task is saved */}
      {task && !isNew && (
        <>
          {/* Notes & Conclusion section */}
          <div className={classes.section}>
            <h3 className={classes.sectionTitle}>Muistiinpanot</h3>
            <div className={classes.formGrid}>
              <div className={classes.field}>
                <label>Omat muistiinpanot</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Kirjaa tähän omia havaintoja tutkimuksen aikana..."
                  className={classes.textarea}
                  rows={3}
                />
              </div>
              <div className={classes.field}>
                <label>Johtopäätöksen perustelut</label>
                <textarea
                  value={conclusion}
                  onChange={(e) => setConclusion(e.target.value)}
                  placeholder="Miksi toimiala avataan / ei avata? Lopputuloksen perustelut."
                  className={classes.textarea}
                  rows={3}
                />
              </div>
            </div>
            <div className={classes.actionsRow}>
              <Button onClick={handleUpdateFields} disabled={saving} size={EButtonSize.SMALL}>
                {saving ? 'Tallennetaan...' : 'Tallenna muistiinpanot'}
              </Button>
              <Button 
                onClick={handleSetNoGo} 
                disabled={saving} 
                color={EButtonColor.DANGER}
                size={EButtonSize.SMALL}
              >
                🚫 Merkitse NO-GO
              </Button>
            </div>
          </div>

          {/* Workbook Sources Panel */}
          <WorkbookSourcesPanel
            taskId={task.id}
            sources={task.workbookSources || []}
            onSourcesChanged={() => fetchTask()}
          />

          {/* Add Agent Task Panel */}
          <AddAgentTaskPanel
            onAddTask={handleAddAgentTask}
            sources={task.workbookSources || []}
            insights={task.insights || []}
          />

          {/* Agent Tasks list */}
          <div className={classes.section}>
            <h3 className={classes.sectionTitle}>
              Tutkimustehtävät ({agentTasks.length})
            </h3>
            {agentTasks.length === 0 ? (
              <p className={classes.emptyMessage}>
                Ei vielä tutkimustehtäviä. Lisää ensimmäinen yllä olevasta paneelista.
              </p>
            ) : (
              agentTasks.map((agentTask) => (
                <AgentTaskCard
                  key={agentTask.id}
                  agentTask={agentTask}
                  insight={findInsightForTask(agentTask)}
                  onDelete={handleDeleteAgentTask}
                  onRefresh={handleRefreshAgentTask}
                />
              ))
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export default MarketResearchWorkbench;
